import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatMoney } from '../lib/format'
import { canApprove } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'

interface ChargeRow {
  id: string
  code: string
  label_fr: string
  label_en: string
  kind: 'pourcentage' | 'fixe'
  rate: string | number | null
  fixed_amount: number | null
  payee: string | null
  requires_receipt: boolean
  sort_order: number
  effective_from: string
  effective_to: string | null
  active: boolean
  source_note: string | null
  a_verifier: boolean
}

async function load(): Promise<ChargeRow[]> {
  const { data, error } = await supabase
    .from('recurring_charges')
    .select(
      'id, code, label_fr, label_en, kind, rate, fixed_amount, payee,' +
        ' requires_receipt, sort_order, effective_from, effective_to,' +
        ' active, source_note, a_verifier',
    )
    .order('sort_order')
    .order('effective_from', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as ChargeRow[]
}

/**
 * Les charges deduites des loyers chaque mois.
 *
 * En lecture pour tous, modifiables par le proprietaire seul : ce sont
 * des taux et des montants d'argent, comme les comptes bancaires et
 * les taux d'imposition. La RLS l'impose deja, cet ecran le dit.
 *
 * Un changement de taux ne modifie pas la ligne en cours : il en cree
 * une nouvelle avec sa date d'effet. C'est ce qui permet de retrouver,
 * des annees plus tard, quel taux s'appliquait a quel mois.
 */
export function RecurringChargesScreen({ onBack }: { onBack: () => void }) {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const { data, loading, error, reload } = useAsync(load)

  const peutModifier = canApprove(profile?.role ?? null)
  const aujourdhui = new Date().toISOString().slice(0, 10)

  return (
    <div className="screen">
      <button type="button" className="backlink" onClick={onBack}>
        ← {t('back')}
      </button>

      <h1 className="screen__title">{t('recurring_title')}</h1>
      <p className="muted">{t('recurring_intro')}</p>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (data ?? []).length === 0 && (
        <EmptyState title={t('recurring_empty')} />
      )}

      {(data ?? []).length > 0 && (
        <div className="stack">
          {(data ?? []).map((c) => {
            const enVigueur =
              c.active &&
              c.effective_from <= aujourdhui &&
              (c.effective_to === null || c.effective_to > aujourdhui)
            const taux = c.rate === null ? null : Number(c.rate)

            return (
              <div key={c.id} className="card">
                <div className="card__row">
                  <span className="list__name">
                    {lang === 'fr' ? c.label_fr : c.label_en}
                  </span>
                  <span className={`chip ${enVigueur ? 'chip--ok' : 'chip--warn'}`}>
                    {enVigueur ? t('lease_status_actif') : t('lease_status_termine')}
                  </span>
                </div>

                <span className="card__figure card__figure--small">
                  {c.kind === 'pourcentage' && taux !== null
                    ? `${(taux * 100).toFixed(taux * 100 % 1 === 0 ? 0 : 2)} %`
                    : formatMoney(c.fixed_amount)}
                </span>

                {c.kind === 'pourcentage' && taux !== null && (
                  <p className="muted">
                    {t('recurring_percent_of', {
                      rate: (taux * 100).toFixed(taux * 100 % 1 === 0 ? 0 : 2),
                    })}
                  </p>
                )}

                <dl className="deflist">
                  {c.payee && (
                    <>
                      <dt>{t('settlement_payee')}</dt>
                      {/* Nom issu de la base, jamais traduit. */}
                      <dd>{c.payee}</dd>
                    </>
                  )}
                  <dt>{t('lease_start')}</dt>
                  <dd>{formatDate(c.effective_from)}</dd>
                  {c.effective_to && (
                    <>
                      <dt>{t('lease_end')}</dt>
                      <dd>{formatDate(c.effective_to)}</dd>
                    </>
                  )}
                </dl>

                {c.requires_receipt && (
                  <span className="chip chip--warn">
                    {t('settlement_receipt_required')}
                  </span>
                )}

                {/* Tout ce qui n'a pas de source documentaire est
                    signale plutot que presente comme acquis. */}
                {c.a_verifier && c.source_note && (
                  <div className="banner banner--warn">
                    {/* La note explique elle-meme ce qui reste a
                        confirmer : pas d'etiquette en plus. */}
                    <span>{c.source_note}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!peutModifier && (data ?? []).length > 0 && (
        <p className="muted">{t('recurring_owner_only')}</p>
      )}
    </div>
  )
}
